import { motion } from "framer-motion";
import { Mic, MicOff } from "lucide-react";
import { useSpeechRecognition } from "../lib/useSpeechRecognition";
import { toast } from "sonner";

/**
 * MicButton — voice-to-text mic that appends transcribed speech via onTranscript.
 * Hides itself silently if the browser doesn't support Web Speech API (avoids confusing UI).
 *
 *   <MicButton onTranscript={(text) => setValue((v) => v + ' ' + text)} />
 */
export const MicButton = ({ onTranscript, className = "", size = "md", testId = "mic-btn" }) => {
  const { supported, listening, interim, error, start, stop } = useSpeechRecognition({
    onResult: (text, isFinal) => {
      if (isFinal && text) onTranscript?.(text);
    },
    continuous: true,
  });

  if (!supported) return null;

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (listening) {
      stop();
    } else {
      start();
      // Friendly nudge if mic perms not granted yet — error fires async
      setTimeout(() => {
        if (error === "not-allowed" || error === "service-not-allowed") {
          toast.error("Microphone permission denied. Enable it in your browser settings.");
        }
      }, 800);
    }
  };

  const sizeCls = size === "sm" ? "h-9 w-9" : "h-11 w-11";
  const iconCls = size === "sm" ? "h-4 w-4" : "h-[18px] w-[18px]";

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      data-listening={listening || undefined}
      aria-label={listening ? "Stop voice input" : "Start voice input"}
      title={listening ? `Listening… ${interim || "speak now"}` : "Speak instead of typing"}
      className={`relative shrink-0 ${sizeCls} rounded-full inline-flex items-center justify-center transition active:scale-[0.94] ${
        listening
          ? "bg-[#FE0979] text-white shadow-[0_0_24px_rgba(254,9,121,0.55)]"
          : "bg-white/8 text-zinc-200 hover:bg-white/15 border border-white/10"
      } ${className}`}
    >
      {listening ? (
        <>
          {/* Pulsing rings */}
          <motion.span
            initial={{ opacity: 0.6, scale: 1 }}
            animate={{ opacity: 0, scale: 1.7 }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeOut" }}
            className="absolute inset-0 rounded-full bg-[#FE0979]/50"
          />
          <motion.span
            initial={{ opacity: 0.4, scale: 1 }}
            animate={{ opacity: 0, scale: 2.2 }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut", delay: 0.25 }}
            className="absolute inset-0 rounded-full bg-[#FE0979]/30"
          />
          <Mic className={`${iconCls} relative z-10`} />
        </>
      ) : error === "not-allowed" || error === "service-not-allowed" ? (
        <MicOff className={iconCls} />
      ) : (
        <Mic className={iconCls} />
      )}
    </button>
  );
};
